import {
  useUsersCustomerCreateInvitation,
  useUsersCustomerinvitationsCreationEntitiesQuery,
} from "@/lib/api/requests/users.requests";
import { Button } from "@/ui-components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui-components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "@/lib/toast";
import z from "zod";

const CreateCustomerInvitationDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: (refetchData: boolean) => void;
}) => {
  const { data: creationEntities } = useUsersCustomerinvitationsCreationEntitiesQuery();

  const onFormSubmit = () => {
    onClose(true);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nutzer einladen</DialogTitle>
        </DialogHeader>
        <CreateInvitationForm onFormSubmit={onFormSubmit} creationEntities={creationEntities} />
      </DialogContent>
    </Dialog>
  );
};

const CreateInvitationForm = ({ onFormSubmit }: { onFormSubmit: () => void; creationEntities: any }) => {
  const { mutate: createInvitation } = useUsersCustomerCreateInvitation();

  const createInvitationSchema = z.object({
    firstName: z.string().min(1, {
      message: "Vorname ist erforderlich",
    }),
    lastName: z.string().min(1, {
      message: "Nachname ist erforderlich",
    }),
    mail: z.string().email("Bitte geben Sie Ihre Email-Adresse ein"),
  });

  type CreateInvitationSchema = z.infer<typeof createInvitationSchema>;

  const form = useForm<CreateInvitationSchema>({
    resolver: zodResolver(createInvitationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      mail: "",
    },
  });

  const onSubmit = async (data: CreateInvitationSchema) => {
    createInvitation(
      {
        firstName: data.firstName,
        lastName: data.lastName,
        mail: data.mail,
      },
      {
        onSuccess: () => {
          toast.success("Nutzer wurde erfolgreich eingeladen");
          onFormSubmit();
        },
        onError: (error) => {
          toast.error("Nutzer konnte nicht eingeladen werden", { error });
        },
      }
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel isOptional={false}>Vorname</FormLabel>
                <FormControl>
                  <Input placeholder="Max" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel isOptional={false}>Nachname</FormLabel>
                <FormControl>
                  <Input placeholder="Mustermann" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="w-full">
          <FormField
            control={form.control}
            name="mail"
            render={({ field }) => (
              <FormItem>
                <FormLabel isOptional={false}>Email</FormLabel>
                <FormControl>
                  <Input placeholder="max.mustermann@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="w-full flex justify-end mt-4">
          <Button type="submit">Nutzer einladen</Button>
        </div>
      </form>
    </Form>
  );
};

export default CreateCustomerInvitationDialog;
